type SupportPayloadWithAttachments = {
  attachments?: File[];
  attachment_files?: string[];
};

type SupportExportParams = {
  format?: string;
  status?: string;
  priority?: string | string[];
  category?: number | number[];
  assignee?: 'me' | 'unassigned';
  assigned_to?: string;
  search?: string;
  date_from?: string;
  date_to?: string;
  ordering?: string;
  ticket_numbers?: string[];
};

type SupportTicketQueryParams = {
  status?: string;
  priority?: string[];
  category?: number[];
  search?: string;
  date_from?: string;
  date_to?: string;
  date_range?: [string, string];
  assignee?: 'me' | 'unassigned';
  assigned_to?: string;
  created_by?: number;
  page?: number;
  page_size?: number;
  ordering?: string;
};

const MAX_SUPPORT_SEARCH_QUERY_LENGTH = 200;
const MAX_SUPPORT_ROUTE_SUBJECT_LENGTH = 255;
const SUPPORT_SEARCH_CONTROL_CHARS_RE = /[\x00-\x1f\x7f]/g;
const SUPPORT_SEARCH_HTML_TAG_RE = /<[^>]+>/g;
const SUPPORT_SEARCH_ATTACK_MARKERS_RE = /--+|\/\*|\*\/|\.{2,}/g;
const SUPPORT_SEARCH_STRAY_SLASH_RE = /(^|\s)\/+|\/+(?=\s|$)/g;
const SUPPORT_SEARCH_UNSAFE_CHARS_RE = /[^\w\u0600-\u06FF\s./@+-]/g;
const SUPPORT_ROUTE_SUBJECT_UNSAFE_CHARS_RE = /[^\w\u0600-\u06FF\s.,:/@+?!-]/g;
const SUPPORT_TICKET_NUMBER_RE = /^TK-(?:\d{6}-[A-Z0-9]{6}|\d{8})$/;
const SUPPORT_POSITIVE_INTEGER_RE = /^\d+$/;

function stripSupportSearchAttackMarkers(query: string): string {
  return query.replace(SUPPORT_SEARCH_ATTACK_MARKERS_RE, ' ').replace(SUPPORT_SEARCH_STRAY_SLASH_RE, ' ');
}

export function normalizeSupportSearchQuery(query: string): string {
  return stripSupportSearchAttackMarkers(
    query
    .slice(0, MAX_SUPPORT_SEARCH_QUERY_LENGTH)
    .replace(SUPPORT_SEARCH_CONTROL_CHARS_RE, ' ')
    .replace(SUPPORT_SEARCH_HTML_TAG_RE, ' ')
  )
    .replace(SUPPORT_SEARCH_UNSAFE_CHARS_RE, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function normalizeSupportTicketQueryValue(value: unknown): string | null {
  const rawValue = Array.isArray(value) ? value[0] : value;
  if (typeof rawValue !== 'string') return null;

  const ticketNumber = rawValue.trim().toUpperCase();
  return SUPPORT_TICKET_NUMBER_RE.test(ticketNumber) ? ticketNumber : null;
}

export function normalizeSupportRouteSubjectQueryValue(value: unknown): string {
  const rawValue = Array.isArray(value) ? value[0] : value;
  if (typeof rawValue !== 'string') return '';

  return rawValue
    .slice(0, MAX_SUPPORT_ROUTE_SUBJECT_LENGTH)
    .replace(SUPPORT_SEARCH_CONTROL_CHARS_RE, ' ')
    .replace(SUPPORT_SEARCH_HTML_TAG_RE, ' ')
    .replace(SUPPORT_ROUTE_SUBJECT_UNSAFE_CHARS_RE, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function normalizeSupportRouteCategoryQueryValue(value: unknown): number | null {
  const rawValue = Array.isArray(value) ? value[0] : value;
  if (typeof rawValue !== 'string' && typeof rawValue !== 'number') return null;

  const categoryValue = String(rawValue).trim();
  if (!SUPPORT_POSITIVE_INTEGER_RE.test(categoryValue)) return null;

  const categoryId = Number(categoryValue);
  return Number.isSafeInteger(categoryId) && categoryId > 0 ? categoryId : null;
}

export function buildSupportAttachmentPayload<T extends SupportPayloadWithAttachments>(
  payload: T,
  uploadedFileIds: string[]
): Omit<T, 'attachments'> & { attachment_files?: string[] } {
  const { attachments: _attachments, ...apiPayload } = payload;

  if (uploadedFileIds.length > 0) {
    return {
      ...apiPayload,
      attachment_files: uploadedFileIds
    };
  }

  return apiPayload;
}

export function buildSupportExportParams<T extends SupportExportParams>(
  params?: T
): Omit<T, 'format'> & { export_format?: string } {
  if (!params) {
    return {} as Omit<T, 'format'> & { export_format?: string };
  }

  const { format, assigned_to: _assignedTo, ...apiParams } = params;

  if (format) {
    return {
      ...apiParams,
      export_format: format
    } as Omit<T, 'format'> & { export_format?: string };
  }

  return apiParams as Omit<T, 'format'> & { export_format?: string };
}

export function buildSupportTicketQueryParams(
  params?: SupportTicketQueryParams
): Record<string, unknown> {
  if (!params) return {};

  const {
    priority,
    category,
    date_range,
    assigned_to: _assignedTo,
    created_by: _createdBy,
    assignee,
    ...apiParams
  } = params;

  const queryParams: Record<string, unknown> = { ...apiParams };

  if (typeof params.search === 'string') {
    const safeSearch = normalizeSupportSearchQuery(params.search);
    if (safeSearch) {
      queryParams.search = safeSearch;
    } else {
      delete queryParams.search;
    }
  }

  if (assignee === 'me' || assignee === 'unassigned') {
    queryParams.assignee = assignee;
  }

  if (date_range?.[0]) {
    queryParams.date_from = date_range[0];
  }

  if (date_range?.[1]) {
    queryParams.date_to = date_range[1];
  }

  if (priority?.length) {
    queryParams['priority[]'] = priority;
  }

  if (category?.length) {
    queryParams['category[]'] = category;
  }

  return queryParams;
}
